import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth.tsx";

interface NavItem {
  to: string;
  label: string;
}

interface NavGroup {
  name: string;
  icon: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    name: "Platform",
    icon: "◈",
    items: [
      { to: "/events", label: "Event Log" },
      { to: "/workflows", label: "Workflows" },
      { to: "/jobs", label: "AI Jobs" },
      { to: "/approvals", label: "Approvals" },
    ],
  },
  {
    name: "CRM",
    icon: "◆",
    items: [
      { to: "/crm", label: "Overview" },
      { to: "/crm/companies", label: "Companies" },
      { to: "/crm/contacts", label: "Contacts" },
      { to: "/crm/leads", label: "Leads" },
      { to: "/crm/proposals", label: "Proposals" },
      { to: "/crm/clients", label: "Clients" },
      { to: "/crm/projects", label: "Projects" },
    ],
  },
  {
    name: "Marketing",
    icon: "▲",
    items: [
      { to: "/marketing", label: "Overview" },
      { to: "/marketing/discovery", label: "Discovery" },
      { to: "/marketing/content", label: "Content" },
      { to: "/marketing/campaigns", label: "Campaigns" },
      { to: "/marketing/seo", label: "SEO" },
      { to: "/marketing/outreach", label: "Outreach" },
    ],
  },
  {
    name: "Knowledge",
    icon: "●",
    items: [
      { to: "/knowledge", label: "Overview" },
      { to: "/knowledge/documents", label: "Documents" },
      { to: "/knowledge/search", label: "Search" },
    ],
  },
  {
    name: "Delivery",
    icon: "■",
    items: [
      { to: "/delivery", label: "Overview" },
      { to: "/delivery/requirements", label: "Requirements" },
      { to: "/delivery/sprints", label: "Sprints" },
      { to: "/delivery/tasks", label: "Tasks" },
      { to: "/delivery/milestones", label: "Milestones" },
      { to: "/delivery/reviews", label: "Reviews" },
      { to: "/delivery/docs", label: "Docs" },
      { to: "/delivery/qa", label: "QA" },
      { to: "/delivery/release", label: "Release" },
    ],
  },
  {
    name: "Operations",
    icon: "⬡",
    items: [
      { to: "/ops", label: "Overview" },
      { to: "/ops/agents", label: "Agents" },
      { to: "/ops/jobs", label: "Jobs" },
      { to: "/ops/scheduler", label: "Scheduler" },
      { to: "/ops/queue", label: "Queue" },
      { to: "/ops/tools", label: "Tools" },
      { to: "/ops/planner", label: "Planner" },
      { to: "/ops/costs", label: "Costs" },
      { to: "/ops/metrics", label: "Metrics" },
      { to: "/ops/health", label: "Health" },
    ],
  },
  {
    name: "Finance",
    icon: "$",
    items: [
      { to: "/finance", label: "Overview" },
      { to: "/finance/quotes", label: "Quotes" },
      { to: "/finance/contracts", label: "Contracts" },
      { to: "/finance/invoices", label: "Invoices" },
      { to: "/finance/payments", label: "Payments" },
      { to: "/finance/expenses", label: "Expenses" },
      { to: "/finance/reports", label: "Reports" },
    ],
  },
  {
    name: "Support",
    icon: "♥",
    items: [
      { to: "/support", label: "Overview" },
      { to: "/support/tickets", label: "Tickets" },
      { to: "/support/notifications", label: "Notifications" },
      { to: "/support/meetings", label: "Meetings" },
      { to: "/support/sla", label: "SLA" },
      { to: "/support/health", label: "Health" },
      { to: "/support/activity", label: "Activity" },
    ],
  },
  {
    name: "Admin",
    icon: "⚙",
    items: [
      { to: "/admin", label: "Overview" },
      { to: "/admin/orgs", label: "Orgs" },
      { to: "/admin/api-keys", label: "API Keys" },
      { to: "/admin/webhooks", label: "Webhooks" },
      { to: "/admin/subscription", label: "Subscription" },
      { to: "/admin/plugins", label: "Plugins" },
      { to: "/admin/audit", label: "Audit" },
      { to: "/admin/usage", label: "Usage" },
      { to: "/admin/system-health", label: "System" },
    ],
  },
];

function groupContainingPath(pathname: string): string | null {
  for (const group of NAV_GROUPS) {
    if (group.items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))) {
      return group.name;
    }
  }
  return null;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const active = groupContainingPath(location.pathname);
    return new Set(active ? [active] : []);
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Keep the group containing the current route expanded when navigating directly (e.g. refresh, deep link).
  useEffect(() => {
    const active = groupContainingPath(location.pathname);
    if (active != null) {
      setOpenGroups((prev) => (prev.has(active) ? prev : new Set(prev).add(active)));
    }
  }, [location.pathname]);

  function toggleGroup(name: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-gray-800 bg-gray-950/95 px-4 py-3 backdrop-blur">
        <button
          onClick={() => setSidebarOpen((v) => !v)}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-900 hover:text-white lg:hidden"
          aria-label="Toggle navigation"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M2.5 5h15M2.5 10h15M2.5 15h15" strokeLinecap="round" />
          </svg>
        </button>

        <Link to="/" className="shrink-0 text-lg font-bold tracking-tight text-indigo-400">
          AIOS
        </Link>

        <div className="flex-1" />

        {user != null && (
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-gray-400 sm:inline">{user.email}</span>
            <span className="rounded bg-indigo-900/50 px-1.5 py-0.5 text-xs font-medium text-indigo-300">
              {user.role}
            </span>
            <button
              onClick={() => void handleLogout()}
              className="text-sm text-gray-400 transition-colors hover:text-white"
            >
              Sign out
            </button>
          </div>
        )}
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 top-[57px] z-20 w-64 shrink-0 overflow-y-auto border-r border-gray-800 bg-gray-950 pb-8 transition-transform duration-200 lg:sticky lg:top-[57px] lg:h-[calc(100vh-57px)] lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} `}
        >
          <nav className="px-3 py-4">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `mb-3 flex items-center gap-2 rounded px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-indigo-600 text-white"
                    : "text-gray-300 hover:bg-gray-900 hover:text-white"
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span className="text-indigo-400">⌂</span> Dashboard
            </NavLink>

            <div className="space-y-1">
              {NAV_GROUPS.map((group) => {
                const isOpen = openGroups.has(group.name);
                const isGroupActive = groupContainingPath(location.pathname) === group.name;
                return (
                  <div key={group.name}>
                    <button
                      onClick={() => toggleGroup(group.name)}
                      className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide transition-colors ${
                        isGroupActive ? "text-indigo-300" : "text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      <span className="w-3 text-center text-[10px]">{group.icon}</span>
                      <span className="flex-1">{group.name}</span>
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        className={`shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                      >
                        <path
                          d="M3 1l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {isOpen && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-800 pl-3">
                        {group.items.map((item) => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === `/${group.name.toLowerCase()}`}
                            className={({ isActive }) =>
                              `block rounded px-2.5 py-1.5 text-sm transition-colors ${
                                isActive
                                  ? "bg-indigo-600/20 font-medium text-indigo-300"
                                  : "text-gray-400 hover:bg-gray-900 hover:text-white"
                              }`
                            }
                            onClick={() => setSidebarOpen(false)}
                          >
                            {item.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-10 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Content */}
        <main className="min-w-0 flex-1 px-6 py-8 lg:px-10">
          <div className="mx-auto max-w-6xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
